/**
 * @module api/domain/dashboard
 * @description 대시보드 관련 API
 *   - AS 현황 조회 (기간별/날짜별)
 *   - 서비스품질 현황 조회 (기간별/날짜별)
 *   - SLA 준수율 조회 (기간별/날짜별)
 *   - 파트너별 KPI 조회 (기간별/날짜별)
 *   - 지역별 히트맵 조회 (기간별/날짜별)
 *   - 트렌드 분석 조회 (기간별/날짜별)
 */
import { getApi } from '@/api/api'
import { buildQueryString } from '@/mixin/mixins'

/** 대시보드 AS 현황 조회 (일, 주, 월, 년) */
export const getRepairRequestByStatusToDashboard = async (day) => {
  const url = '/dashboard/repair-request?periodStatus=' + day
  return await getApi(url)
}

/** 대시보드 AS 현황 조회 (선택 날짜) */
export const getRepairRequestByStatusAndBetweenDateToDashboard = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/dashboard/repair-request/between' + queryString
  return await getApi(url)
}

/** 대시보드 서비스품질 현황 조회 (일, 주, 월, 년) */
export const getServiceQualityToDashboard = async (day) => {
  const url = '/dashboard/service-quality?periodStatus=' + day
  return await getApi(url)
}

/** 대시보드 서비스품질 현황 조회 (선택 날짜) */
export const getServiceQualityBYBetweenDateToDashboard = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })

  const url = '/dashboard/service-quality/between' + queryString
  return await getApi(url)
}

// === SLA 준수율 ===

/** SLA 준수율 조회 (기간 프리셋) */
export const getSlaCompliance = async (periodStatus) => {
  const url = '/dashboard/sla-compliance?periodStatus=' + periodStatus
  return await getApi(url)
}

/** SLA 준수율 조회 (선택 날짜) */
export const getSlaComplianceBetween = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })
  const url = '/dashboard/sla-compliance/between' + queryString
  return await getApi(url)
}

// === 파트너별 KPI ===

/** 파트너별 KPI 조회 (기간 프리셋) */
export const getPartnerKpi = async (periodStatus) => {
  const url = '/dashboard/partner-kpi?periodStatus=' + periodStatus
  return await getApi(url)
}

/** 파트너별 KPI 조회 (선택 날짜) */
export const getPartnerKpiBetween = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })
  const url = '/dashboard/partner-kpi/between' + queryString
  return await getApi(url)
}

// === 지역별 히트맵 ===

/** 지역별 히트맵 데이터 조회 (기간 프리셋) */
export const getRegionHeatmap = async (periodStatus) => {
  const url = '/dashboard/region-heatmap?periodStatus=' + periodStatus
  return await getApi(url)
}

/** 지역별 히트맵 데이터 조회 (선택 날짜) */
export const getRegionHeatmapBetween = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })
  const url = '/dashboard/region-heatmap/between' + queryString
  return await getApi(url)
}

// === 트렌드 분석 ===

/** 트렌드 분석 데이터 조회 (기간 프리셋) */
export const getTrend = async (periodStatus) => {
  const url = '/dashboard/trend?periodStatus=' + periodStatus
  return await getApi(url)
}

/** 트렌드 분석 데이터 조회 (선택 날짜) */
export const getTrendBetween = async (queryObject) => {
  const queryString = buildQueryString({ ...queryObject })
  const url = '/dashboard/trend/between' + queryString
  return await getApi(url)
}

// === v2 ADDENDUM — SURINP-183 Phase 5 신규 (2026-04-27) ===

/**
 * 오늘 요약 KPI (Hero) 조회. (SURINP-183 Phase 5-C, 댓글 #11761)
 *
 * 백엔드 캐시: 60초. RBAC: userGroupId 필터 (일반 사용자는 자기 파트너만).
 * 응답 형태:
 *   {
 *     fetchedAt: string,
 *     receipt: number,        // 오늘 접수 건수
 *     complete: number,       // 오늘 완료 건수
 *     inProgress: number,     // 진행 중
 *     unassigned: number,     // 미배정
 *     slaImminent: number     // SLA 임박 건수
 *   }
 *
 * @returns {Promise<object>}
 */
export const getDashboardTodaySummary = async () => {
  const url = '/dashboard/today-summary'
  return await getApi(url)
}

/**
 * 고객사별 접수 현황 (5메트릭) 조회. (SURINP-183 Phase 5-C, 댓글 #11761)
 *
 * 백엔드 캐시: 5분. RBAC: 일반 사용자는 자기 파트너 1건만 응답.
 * processType 분류 (댓글 #11764 정정): cs-back은 RepairRequestProcessType의
 * CANCELED_BY_CUSTOMER / CANCELED_BEFORE_VISIT / CANCELED_AFTER_VISIT 3종을
 * `cancelled` 필드에 합산하여 반환한다.
 *
 * 응답 형태:
 *   {
 *     fetchedAt: string,
 *     periodStatus: string,
 *     items: Array<{
 *       partnerId: number,
 *       partnerName: string,
 *       total: number,
 *       pending: number,
 *       cancelled: number,
 *       completed: number,
 *       completionRate: number   // 0 ~ 100
 *     }>
 *   }
 *
 * @param {string} periodStatus - 'TODAY' | 'WEEK' | 'MONTH' | 'YEAR'
 * @returns {Promise<object>}
 */
export const getCustomerStatus = async (periodStatus) => {
  const url = '/dashboard/customer-status?periodStatus=' + encodeURIComponent(periodStatus)
  return await getApi(url)
}

/**
 * 실시간 활동 피드 조회 (페이지 넘김). (SURINP-183 Phase 5-C, 댓글 #11766 확정)
 *
 * 백엔드 캐시: 30초. ORDER BY updated_at DESC.
 * 페이지네이션: 1-based. 백엔드 하드 캡 50건 (totalCount, totalPages 최대 5).
 * 클라이언트가 잘못된 page를 요청하면 백엔드는 마지막 유효 페이지로 클램프하고
 * 응답의 page 필드에 실제 사용된 값을 반환한다 (클라이언트는 이 값으로 sync).
 *
 * 응답 형태:
 *   {
 *     fetchedAt: string,
 *     page: number,         // 1-based, 백엔드 클램프 후 값
 *     size: number,
 *     totalCount: number,   // 최대 50
 *     totalPages: number,   // 최대 5
 *     hasPrev: boolean,
 *     hasNext: boolean,
 *     items: Array<{
 *       sourceId: number,
 *       type: 'RECEIPT' | 'ASSIGN' | 'VISIT_PLAN' | 'COMPLETE' | 'DELAY',
 *       timestamp: string,
 *       urgent: boolean,    // 서버 계산 — SLA 임박/지연
 *       summary: string,
 *       partnerName?: string
 *     }>
 *   }
 *
 * @param {number} [page=1] - 1-based 페이지 번호
 * @param {number} [size=10] - 페이지 크기 (시안 L1496 data-visible="10")
 * @returns {Promise<object>}
 */
export const getActivityFeed = async (page = 1, size = 10) => {
  const url = `/dashboard/activity-feed?page=${encodeURIComponent(page)}&size=${encodeURIComponent(size)}`
  return await getApi(url)
}

/**
 * 기사 방문 캘린더 — 월간 일자별 visit 요약 조회.
 * (SURINP-183 Phase 5-C → v3 ADDENDUM, SURINP-182 댓글 #11804로 응답 구조 확장)
 *
 * 백엔드 캐시: 5분. RBAC: ADMIN-only (일반 사용자에게는 403 반환).
 * 클라이언트는 useDashboardPermission().isAdmin === false면 마운트조차 하지 않으나
 * 백엔드 RBAC와 이중 검증 의도.
 *
 * 응답 형태 (v3 — visits/totalCount 추가):
 *   {
 *     fetchedAt: string,
 *     yearMonth: string,         // 'YYYY-MM'
 *     days: Array<{
 *       date: string,            // 'YYYY-MM-DD'
 *       visitCount: number,      // 호환 유지 (= totalCount 동일)
 *       totalCount?: number,     // 신규 — 일자별 전체 방문 건수
 *       visits?: Array<{         // 신규 — 상위 N건(cap=5, server-sorted)
 *         requestId: number,
 *         visitTime: string|null,// CodeSub `repairVisitTimeCode` 슬롯명 (예: '오전','오후'). 미설정 시 null
 *         processStatus: 'VISIT_PLANNED' | 'IN_PROGRESS' | 'COMPLETED',
 *         technicianName: string,
 *         partnerName: string,
 *         customerName?: string
 *       }>
 *     }>
 *   }
 *
 * 데이터 범위 — 백엔드가 RepairRequestStatusType으로 사전 필터:
 *   포함: R_VISIT, R_REVISIT (→ VISIT_PLANNED) / R_REPAIR (→ IN_PROGRESS) / R_COMPLETE, R_CONFIRM (→ COMPLETED)
 *   제외: R_REQUEST, R_ASSIGNMENT, R_VISIT_WAIT (status 필터로 사전 제외) + cancelled 3종 + TRANSFERRED_REQUEST.
 *
 * 정렬 (서버 위임): visitTimeSortKey(CodeSub.subCode) ASC NULLS LAST → requestId ASC.
 *   슬롯명 정렬은 subCode가 시각 순서 인코딩이라는 전제 — CodeSub 마스터 등록 시 subCode 순서 유지 필요.
 *
 * Backward compat: visits/totalCount 부재 시 frontend는 visitCount만으로 카운트 뱃지 fallback.
 *
 * @param {string} yearMonth - 'YYYY-MM' 형식
 * @returns {Promise<object>}
 */
export const getTechnicianCalendar = async (yearMonth) => {
  const url = '/dashboard/technician-calendar?yearMonth=' + encodeURIComponent(yearMonth)
  return await getApi(url)
}

/**
 * 기사 방문 캘린더 — 일자별 방문 상세 조회 (모달 lazy load).
 * (SURINP-183 Phase 5-C, 댓글 #11761)
 *
 * 백엔드 캐시: 60초. RBAC: ADMIN-only.
 * 응답 형태:
 *   {
 *     fetchedAt: string,
 *     date: string,
 *     visits: Array<{
 *       requestId: number,
 *       visitTime: string|null,   // CodeSub `repairVisitTimeCode` 슬롯명 (예: '오전','오후'). 미설정 시 null
 *       processStatus: string,
 *       partnerName: string,
 *       customerName: string,
 *       productGroup: string,
 *       symptom: string,
 *       technicianName: string,
 *       contact: string,
 *       region: string
 *     }>
 *   }
 *
 * @param {string} date - 'YYYY-MM-DD' 형식
 * @returns {Promise<object>}
 */
export const getTechnicianCalendarDay = async (date) => {
  const url = '/dashboard/technician-calendar/' + encodeURIComponent(date)
  return await getApi(url)
}
